<script setup lang="ts">
/**
 * The menus worth having again (SPEC §6.16, §8.15).
 *
 * A list of names and how big each menu is. What somebody scanning this is
 * actually deciding is which of these they have got tired of, so "last used"
 * earns its place beside the counts — and a template the rotation is currently
 * pointing at says so, because archiving that one has consequences the list
 * would otherwise hide.
 */
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import EmptyState from '@/components/EmptyState.vue'
import {
  ApiError,
  endpoints,
  type MealRotationResponse,
  type MealTemplateSummaryResponse,
} from '@/api'

const route = useRoute()
const router = useRouter()

const groupId = computed(() => String(route.params.groupId))

const templates = ref<MealTemplateSummaryResponse[]>([])
const rotation = ref<MealRotationResponse | null>(null)
const includeArchived = ref(false)
const loading = ref(true)
const busy = ref<string | null>(null)
const error = ref<string | null>(null)
const notice = ref<string | null>(null)

const duplicating = ref<MealTemplateSummaryResponse | null>(null)
const duplicateName = ref('')

const scheduling = ref<MealTemplateSummaryResponse | null>(null)
const scheduleDate = ref('')
const scheduleTime = ref('12:30')

function describe(caught: unknown): string {
  return (caught instanceof ApiError && caught.problem?.title) || 'That did not work.'
}

/**
 * Where this template sits in the rotation, counted from one.
 *
 * Null when it is not in the rotation at all, which is most of them. Joined
 * here rather than asked of the server, because the rotation is one small
 * document the page already needs.
 */
function rotationPosition(templateId: string): number | null {
  const entries = rotation.value?.entries ?? []
  const index = entries.findIndex((e) => e.mealTemplateId === templateId && e.isActive)

  return index < 0 ? null : index + 1
}

function lastUsed(template: MealTemplateSummaryResponse): string {
  if (!template.lastUsedUtc) return 'Never used'

  return `Last used ${new Date(template.lastUsedUtc).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}`
}

async function load() {
  loading.value = true
  error.value = null

  try {
    const [list, order] = await Promise.all([
      endpoints.listMealTemplates(groupId.value, includeArchived.value),
      endpoints.getMealRotation(groupId.value),
    ])

    templates.value = list
    rotation.value = order
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    loading.value = false
  }
}

function edit(template: MealTemplateSummaryResponse) {
  void router.push({
    name: 'meal-template',
    params: { groupId: groupId.value, templateId: template.id },
  })
}

function openDuplicate(template: MealTemplateSummaryResponse) {
  duplicating.value = template
  duplicateName.value = `${template.name} (copy)`
}

async function duplicate() {
  const template = duplicating.value

  if (!template) return

  busy.value = template.id
  duplicating.value = null
  error.value = null

  try {
    const copy = await endpoints.duplicateMealTemplate(groupId.value, template.id, {
      name: duplicateName.value,
    })

    notice.value = `Copied to ${copy.name}.`
    await load()
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    busy.value = null
  }
}

async function setArchived(template: MealTemplateSummaryResponse, archived: boolean) {
  busy.value = template.id
  error.value = null

  try {
    if (archived) {
      await endpoints.archiveMealTemplate(groupId.value, template.id)

      // Archiving takes it out of the rotation too, and somebody who has just
      // pressed this needs to know that happened rather than discovering it
      // three Sundays later (SPEC §6.18).
      notice.value =
        rotationPosition(template.id) === null
          ? `${template.name} is archived.`
          : `${template.name} is archived and out of the rotation.`
    } else {
      await endpoints.unarchiveMealTemplate(groupId.value, template.id)
      notice.value = `${template.name} is back.`
    }

    await load()
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    busy.value = null
  }
}

function openSchedule(template: MealTemplateSummaryResponse) {
  scheduling.value = template
  scheduleDate.value = ''
  scheduleTime.value = '12:30'
}

/**
 * Puts a meal on the calendar with this menu on it.
 *
 * Two calls, because they are two things: a meal exists on a date, and its
 * menu is a copy of a template taken at a moment. Doing it as one endpoint
 * would tie every future way of creating a meal to templates.
 */
async function schedule() {
  const template = scheduling.value

  if (!template || !scheduleDate.value) return

  busy.value = template.id
  scheduling.value = null
  error.value = null

  try {
    const meal = await endpoints.createMealEvent(groupId.value, {
      title: template.name,
      startDate: scheduleDate.value,
      startTime: scheduleTime.value,
      mealTemplateId: template.id,
    })

    // Not confirmed, and it does not need to be: a meal made a moment ago has
    // no menu and therefore nothing anybody has promised to lose (SPEC §6.19,
    // MT-090).
    await endpoints.assignMealTemplateToEvent(groupId.value, meal.id, {
      mealTemplateId: template.id,
      confirm: false,
    })

    // The menu, because a template was just copied onto this meal and looking
    // at it is the point of having done that.
    await router.push({
      name: 'organizer-event-menu',
      params: { groupId: groupId.value, eventId: meal.id },
    })
  } catch (caught) {
    error.value = describe(caught)
    busy.value = null
  }
}

function create() {
  void router.push({
    name: 'meal-template',
    params: { groupId: groupId.value, templateId: 'new' },
  })
}

async function toggleArchived(value: boolean | null) {
  includeArchived.value = Boolean(value)
  await load()
}

void load()
</script>

<template>
  <div class="templates">
    <header class="templates__head">
      <div>
        <h1 class="templates__title">Meal templates</h1>
        <p class="templates__subtitle">
          Menus worth having again. An event copies one rather than pointing at it, so tidying these
          up never rewrites a meal that has already happened.
        </p>
      </div>

      <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" @click="create">
        New template
      </v-btn>
    </header>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="notice" type="success" variant="tonal" class="mb-4">{{ notice }}</v-alert>

    <v-switch
      :model-value="includeArchived"
      label="Show archived"
      color="primary"
      density="compact"
      hide-details
      class="templates__filter"
      @update:model-value="toggleArchived"
    />

    <v-skeleton-loader v-if="loading" type="list-item-two-line@3" />

    <EmptyState
      v-else-if="templates.length === 0 && !error"
      icon="mdi-book-open-variant-outline"
      headline="No templates yet"
      description="A template is a menu you can reuse — the lasagna dinner, the taco bar, the soup lunch. Make one and every meal after it starts from something."
    >
      <template #action>
        <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" @click="create">
          Create a template
        </v-btn>
      </template>
    </EmptyState>

    <ul v-else class="templates__list">
      <li v-for="template in templates" :key="template.id" class="templates__row">
        <div class="templates__row-main">
          <button type="button" class="templates__name" @click="edit(template)">
            {{ template.name }}
          </button>

          <p class="templates__meta">
            <span>{{ template.itemCount }} items in {{ template.sectionCount }} sections</span>
            <span>{{ lastUsed(template) }}</span>
            <span v-if="rotationPosition(template.id)">
              Rotation position {{ rotationPosition(template.id) }}
            </span>
            <v-chip v-if="template.status === 'Archived'" size="x-small" variant="tonal">
              Archived
            </v-chip>
          </p>
        </div>

        <div class="templates__row-actions">
          <v-btn
            :disabled="busy !== null"
            variant="text"
            density="comfortable"
            @click="edit(template)"
          >
            Edit
          </v-btn>
          <v-btn
            :disabled="busy !== null"
            variant="text"
            density="comfortable"
            @click="openDuplicate(template)"
          >
            Duplicate
          </v-btn>
          <v-btn
            v-if="template.status !== 'Archived'"
            :disabled="busy !== null"
            variant="text"
            density="comfortable"
            @click="openSchedule(template)"
          >
            Put on the calendar
          </v-btn>
          <v-btn
            :loading="busy === template.id"
            :disabled="busy !== null"
            variant="text"
            density="comfortable"
            @click="setArchived(template, template.status !== 'Archived')"
          >
            {{ template.status === 'Archived' ? 'Bring back' : 'Archive' }}
          </v-btn>
        </div>
      </li>
    </ul>

    <v-dialog
      :model-value="duplicating !== null"
      max-width="28rem"
      @update:model-value="duplicating = null"
    >
      <v-card>
        <v-card-title tag="h2">Duplicate {{ duplicating?.name }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="duplicateName"
            label="Name"
            variant="outlined"
            density="comfortable"
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="duplicating = null">Cancel</v-btn>
          <v-btn
            :disabled="duplicateName.trim() === ''"
            variant="flat"
            color="primary"
            @click="duplicate"
          >
            Duplicate
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :model-value="scheduling !== null"
      max-width="28rem"
      @update:model-value="scheduling = null"
    >
      <v-card>
        <v-card-title tag="h2">Put {{ scheduling?.name }} on the calendar</v-card-title>
        <v-card-text class="templates__schedule">
          <p class="templates__schedule-hint">
            This makes a draft meal with a copy of the menu. Nobody is told about it until you
            publish it.
          </p>
          <v-text-field
            v-model="scheduleDate"
            type="date"
            label="Date"
            variant="outlined"
            density="comfortable"
            hide-details
          />
          <v-text-field
            v-model="scheduleTime"
            type="time"
            label="Time"
            variant="outlined"
            density="comfortable"
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="scheduling = null">Cancel</v-btn>
          <v-btn :disabled="scheduleDate === ''" variant="flat" color="primary" @click="schedule">
            Make the draft
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.templates {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.templates__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.templates__title {
  font-size: 1.5rem;
  margin: 0;
}

.templates__subtitle,
.templates__empty {
  color: rgb(var(--v-theme-on-surface-variant));
  max-width: 60ch;
  margin: 0.25rem 0 0;
}

.templates__filter {
  align-self: flex-start;
}

.templates__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.templates__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
  border: 1px solid rgb(var(--v-border-color), var(--v-border-opacity));
  border-radius: 0.5rem;
}

.templates__name {
  font-size: 1.05rem;
  font-weight: 600;
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  cursor: pointer;
  text-align: left;
}

.templates__name:hover {
  text-decoration: underline;
}

.templates__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1rem;
  align-items: center;
  color: rgb(var(--v-theme-on-surface-variant));
  font-size: 0.875rem;
  margin: 0.25rem 0 0;
}

.templates__row-actions {
  display: flex;
  flex-wrap: wrap;
}

.templates__schedule {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.templates__schedule-hint {
  color: rgb(var(--v-theme-on-surface-variant));
  margin: 0;
}
</style>
